import axiosInstance from './axiosInstance';
import { TopicCreationDto, TopicUpdateDto } from '@docbay/schemas';

/**
 * API service for managing topics.
 */
export const TopicsAPI = {
  /**
   * Fetches a list of topics for a given professional.
   *
   * @param data - An object containing the professional ID and pagination parameters.
   * @returns A promise that resolves to an Axios response with the topic data.
   */
  getTopics: (data: {
    id: string;
    params: {
      page: number;
      limit: number;
      clinicIds?: string;
    };
  }) => {
    const params = {
      page: data.params.page,
      limit: data.params.limit,
      ...(data.params.clinicIds ? { clinicIds: data.params.clinicIds } : {}),
    };
    return axiosInstance.get(`/professionals/${data.id}/topics`, { params });
  },

  /**
   * Creates a new topic for a given professional.
   *
   * @param params - An object containing the professional ID and the new topic data.
   * @returns A promise that resolves to an Axios response with the created topic data.
   */
  createTopic: (params: { id: string; data: TopicCreationDto }) => {
    return axiosInstance.post(`/professionals/${params.id}/topics`, {
      ...params.data,
    });
  },

  /**
   * Updates an existing topic for a given professional.
   *
   * @param params - An object containing the professional ID, the topic ID to update,
   * and the updated topic data.
   * @returns A promise that resolves to an Axios response with the updated topic data.
   */
  editTopic: (params: { id: string; topicId: string; data: TopicUpdateDto }) =>
    axiosInstance.patch(
      `/professionals/${params.id}/topics/${params.topicId}`,
      params.data,
    ),

  /**
   * Deletes a topic for a given professional.
   *
   * @param params - An object containing the professional ID and the topic ID to delete.
   * @returns A promise that resolves to an Axios response with no data.
   */
  deleteTopic: (params: { id: string; topicId: string }) =>
    axiosInstance.delete(
      `/professionals/${params.id}/topics/${params.topicId}`,
    ),
};
